import type {ReactNode} from 'react'
import React from 'react'
import {clsx} from 'clsx'
import {SoftNavOnlyLink as Link} from '@github-ui/react-router'
import {SkeletonText} from '@primer/react/experimental'
import styles from './ListBlock.module.css'

/* <ListBlock> */
interface ListBlockProps {
  children: ReactNode
  className?: string
  variant?: 'default' | 'expanded'
}

function ListBlock({children, className, variant = 'default'}: ListBlockProps) {
  return <div className={clsx(styles.container, variant === 'expanded' && styles.expanded, className)}>{children}</div>
}

/* <ListBlock.Header> */
interface ListBlockHeaderProps {
  children: ReactNode
  className?: string
}

function ListBlockHeader({children, className}: ListBlockHeaderProps) {
  return <div className={clsx(styles.header, className)}>{children}</div>
}

/* <ListBlock.List> */
interface ListBlockListProps {
  children: ReactNode
  role?: string
  id?: string
  'aria-labelledby'?: string
  loading?: boolean
}

function ListBlockList({children, loading = false, ...props}: ListBlockListProps) {
  if (loading) {
    return (
      <ul className={styles.list} data-testid="list-block-skeleton" {...props}>
        {[...Array(5)].map((_, index) => (
          <ItemSkeleton key={index} />
        ))}
      </ul>
    )
  }

  return (
    <ul className={styles.list} {...props}>
      {children}
    </ul>
  )
}

/* <ListBlock.Item> */
type ListBlockItemLinkProps = {
  href: string
  isExternalLink?: boolean
  children: ReactNode
} & Omit<React.ComponentProps<typeof Link>, 'to'>

type ListBlockItemButtonProps = {
  onClick?: () => void
  children: ReactNode
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>

type ListBlockItemProps = ListBlockItemLinkProps | ListBlockItemButtonProps

function ListBlockItem(props: ListBlockItemProps) {
  let leadingVisual: ReactNode = null
  let title: ReactNode = null
  let description: ReactNode = null
  let metadata: ReactNode = null
  let actions: ReactNode = null

  const childrenArray = React.Children.toArray(props.children)

  for (const child of childrenArray) {
    if (!React.isValidElement(child)) continue

    if (child.type === ItemLeadingVisual) {
      leadingVisual = child
    } else if (child.type === ItemTitle) {
      title = child
    } else if (child.type === ItemDescription) {
      description = child
    } else if (child.type === ItemMetadata) {
      metadata = child
    } else if (child.type === ItemActions) {
      actions = child
    }
  }

  const content = (
    <>
      {leadingVisual}
      {title}
      {description}
      {metadata}
    </>
  )

  if ('href' in props) {
    const {href, isExternalLink = false, ...rest} = props

    // For external links, use an <a> tag
    if (isExternalLink) {
      return (
        <li className={styles.item}>
          <a
            href={href}
            className={clsx(styles.itemContent, styles.itemClickable)}
            target="_self"
            rel="noopener noreferrer"
            {...rest}
          >
            {content}
          </a>
          {actions}
        </li>
      )
    }

    // For internal links, use React Router
    return (
      <li className={styles.item}>
        <Link {...rest} to={href} className={clsx(styles.itemContent, styles.itemClickable)}>
          {content}
        </Link>
        {actions}
      </li>
    )
  }

  if ('onClick' in props) {
    const {onClick, ...rest} = props

    return (
      <li className={styles.item}>
        <button {...rest} className={clsx(styles.itemContent, styles.itemClickable)} onClick={onClick}>
          {content}
        </button>
        {actions}
      </li>
    )
  }

  return (
    <li className={styles.item}>
      <div className={styles.itemContent}>{content}</div>
      {actions}
    </li>
  )
}

/* <ListBlock.Item.Actions> */
interface ItemActionsProps {
  children: ReactNode
}

function ItemActions({children}: ItemActionsProps) {
  return <div className={styles.actions}>{children}</div>
}

/* <ListBlock.Item.Skeleton> */
function ItemSkeleton() {
  return (
    <li className={styles.item}>
      <div className={styles.skeletonItem}>
        <SkeletonText lines={2} />
      </div>
    </li>
  )
}

/* <ListBlock.Item.LeadingVisual> */
interface ItemLeadingVisualProps {
  children: ReactNode
}

function ItemLeadingVisual({children}: ItemLeadingVisualProps) {
  return <div className={styles.leadingVisual}>{children}</div>
}

/* <ListBlock.Item.Title> */
interface ItemTitleProps {
  children: ReactNode
}

function ItemTitle({children}: ItemTitleProps) {
  return <h3 className={styles.title}>{children}</h3>
}

/* <ListBlock.Item.Description> */
interface ItemDescriptionProps {
  children: ReactNode
}

function ItemDescription({children}: ItemDescriptionProps) {
  return <div className={styles.description}>{children}</div>
}

/* <ListBlock.Item.Metadata> */
interface ItemMetadataProps {
  children: ReactNode
}

function ItemMetadata({children}: ItemMetadataProps) {
  return <div className={styles.metadata}>{children}</div>
}

ListBlockItem.LeadingVisual = ItemLeadingVisual
ListBlockItem.Title = ItemTitle
ListBlockItem.Description = ItemDescription
ListBlockItem.SecondaryDescription = ItemMetadata
ListBlockItem.Metadata = ItemMetadata
ListBlockItem.Actions = ItemActions
ListBlockItem.Skeleton = ItemSkeleton

/* <ListBlock.EmptyState> */
interface ListBlockEmptyStateProps {
  children: ReactNode
}

function ListBlockEmptyState({children}: ListBlockEmptyStateProps) {
  return <li className={styles.emptyState}>{children}</li>
}

interface ListBlockNamespace {
  (props: ListBlockProps): React.ReactElement
  Header: typeof ListBlockHeader
  List: typeof ListBlockList
  Item: typeof ListBlockItem & {
    LeadingVisual: typeof ItemLeadingVisual
    Title: typeof ItemTitle
    Description: typeof ItemDescription
    Metadata: typeof ItemMetadata
    Actions: typeof ItemActions
    Skeleton: typeof ItemSkeleton
  }
  EmptyState: typeof ListBlockEmptyState
  displayName?: string
}

const ListBlockWithNamespace = ListBlock as ListBlockNamespace
ListBlockWithNamespace.Header = ListBlockHeader
ListBlockWithNamespace.List = ListBlockList
ListBlockWithNamespace.Item = ListBlockItem
ListBlockWithNamespace.EmptyState = ListBlockEmptyState

ListBlockWithNamespace.displayName = 'ListBlock'
ListBlockHeader.displayName = 'ListBlock.Header'
ListBlockList.displayName = 'ListBlock.List'
ListBlockItem.displayName = 'ListBlock.Item'
ItemLeadingVisual.displayName = 'ListBlock.Item.LeadingVisual'
ItemTitle.displayName = 'ListBlock.Item.Title'
ItemDescription.displayName = 'ListBlock.Item.Description'
ItemMetadata.displayName = 'ListBlock.Item.Metadata'
ItemActions.displayName = 'ListBlock.Item.Actions'
ItemSkeleton.displayName = 'ListBlock.Item.Skeleton'
ListBlockEmptyState.displayName = 'ListBlock.EmptyState'

export default ListBlockWithNamespace
export {ListBlockWithNamespace as ListBlock, ListBlockHeader, ListBlockList, ListBlockItem, ListBlockEmptyState}
